import { Button } from '@/components/ui/button';
import {
  Search, PenLine, Share2, Eye, Link2, Tag, TrendingUp, Bell, GitMerge, FileText,
  Globe, BarChart3, Bot, Users, MessageSquare, LineChart, Languages, Zap,
  Check, X, ArrowRight, Clock, Infinity as InfinityIcon, DollarSign,
} from 'lucide-react';

const OUTCOMES = [
  { icon: Clock, value: 'Minutes', label: 'from idea to published — not weeks' },
  { icon: Zap, value: '30+', label: 'marketing tasks automated for you' },
  { icon: InfinityIcon, value: '24/7', label: 'always on — works while you sleep' },
  { icon: DollarSign, value: '$0', label: 'extra hires, benefits or retainers' },
];

// [feature, in-house hire, agency, web-techservices]
const COMPARE = [
  ['Availability', 'Nights & weekends off, PTO + sick days', 'Business hours, holidays off', '24/7 — nights, weekends, holidays'],
  ['Time to start', '2–4 weeks to hire & train', '1–2 weeks onboarding', 'Minutes'],
  ['Output speed', 'Hours to days per task', '1–2 day turnarounds', 'Idea → published in minutes'],
  ['Channels covered', 'One person, 1–2 skills', 'A team, but siloed', 'SEO, content, social & reporting in one'],
  ['Turnover risk', 'Quits, gets poached', 'Account-manager churn', 'Never quits, never forgets'],
  ['Typical cost', '$4,000–$8,000/mo + benefits', '$5,000–$15,000/mo retainer', 'One flat plan — no headcount'],
];

const CAPABILITIES = [
  ['Keyword research', Search], ['On-page SEO', TrendingUp], ['Internal linking', Link2],
  ['Meta tags & schema', Tag], ['Title-tag rewrites', PenLine], ['Page-2 keyword rescue', Search],
  ['CTR optimization', LineChart], ['Ranking-drop alerts', Bell], ['Cannibalization cleanup', GitMerge],
  ['Content-gap analysis', FileText], ['AI blog writing', PenLine], ['Multilingual content', Languages],
  ['Local landing pages', Globe], ['Instant Google indexing', Zap], ['Social post creation', Share2],
  ['LinkedIn auto-posting', Share2], ['Facebook auto-posting', Share2], ['Review monitoring', Eye],
  ['Competitor tracking', Eye], ['Weekly reporting', BarChart3], ['Portfolio dashboards', BarChart3],
  ['AI visibility checks', Bot], ['Lead capture', Users], ['Email follow-up', MessageSquare],
];

const TEAM = [
  { initials: 'AX', name: 'Atlas', role: 'AI SEO Analyst', icon: Search,
    blurb: 'Reads your Search Console every week — surfaces page-2 rescues, CTR leaks and ranking drops, then fixes them.' },
  { initials: 'QL', name: 'Quill', role: 'AI Content Writer', icon: PenLine,
    blurb: 'Researches real search demand and publishes SEO blog posts in your brand voice — automatically.' },
  { initials: 'EC', name: 'Echo', role: 'AI Social Manager', icon: Share2,
    blurb: 'Writes and schedules LinkedIn & Facebook posts across all your brands so your feeds never go quiet.' },
  { initials: 'SN', name: 'Sentry', role: 'AI Reputation Monitor', icon: Eye,
    blurb: 'Watches reviews, rankings and competitors — and alerts you the moment something moves.' },
];

function StatBand() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="marketing-outcome-stats">
      {OUTCOMES.map((o, i) => (
        <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
          <o.icon className="h-6 w-6 text-blue-600 mx-auto mb-3" />
          <div className="text-3xl font-extrabold text-slate-900">{o.value}</div>
          <div className="mt-1 text-sm text-slate-500">{o.label}</div>
        </div>
      ))}
    </div>
  );
}

function CompareTable() {
  const cols = ['In-house hire', 'Agency', 'Web-TechServices AI'];
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="marketing-compare-table">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="p-4 text-xs font-semibold uppercase tracking-wide text-slate-400"></th>
            {cols.map((c, i) => (
              <th key={i} className={`p-4 text-sm font-bold ${i === 2 ? 'text-blue-700 bg-blue-50' : 'text-slate-600'}`}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPARE.map((row, r) => (
            <tr key={r} className="border-b border-slate-100 last:border-0">
              <td className="p-4 text-sm font-semibold text-slate-700 align-top">{row[0]}</td>
              {[1, 2, 3].map((c) => (
                <td key={c} className={`p-4 text-sm align-top ${c === 3 ? 'bg-blue-50/60 text-slate-900 font-medium' : 'text-slate-500'}`}>
                  <span className="flex items-start gap-2">
                    {c === 3
                      ? <Check className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                      : <X className="h-4 w-4 text-slate-300 shrink-0 mt-0.5" />}
                    {row[c]}
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AlwaysOnMarketingTeam({ compact = false }) {
  if (compact) {
    return (
      <div className="max-w-6xl mx-auto" data-testid="marketing-team-teaser">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1">
            <Bot className="h-3.5 w-3.5" /> Always-On AI Marketing Team
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-slate-900">
            A full marketing team — without the payroll
          </h2>
          <p className="mt-3 text-slate-600">
            Hiring is slow and expensive. Agencies bill by the hour and clock out. Our AI specialists run your
            SEO, content and social <span className="font-semibold text-slate-900">24/7</span> — and only charge one flat plan.
          </p>
        </div>
        <StatBand />
        <div className="mt-10 text-center">
          <a href="/seo-intelligence">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold h-12 px-7 text-base" data-testid="marketing-team-cta">
              Meet your AI marketing team <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-20" data-testid="always-on-marketing-team">
      {/* Outcomes */}
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900">A full marketing team, working while you sleep</h2>
          <p className="mt-3 text-slate-600">You approve. We execute. Across every channel, every week — automatically.</p>
        </div>
        <StatBand />
      </div>

      {/* Comparison */}
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">Your options for ongoing marketing</h3>
          <p className="mt-3 text-slate-600">Same work. None of the overhead, the waiting or the turnover.</p>
        </div>
        <CompareTable />
      </div>

      {/* Capabilities grid */}
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">30+ AI marketing workflows we run for you</h3>
          <p className="mt-3 text-slate-600">Not advice. Not a chatbot. Real execution across your whole marketing stack.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="marketing-capability-grid">
          {CAPABILITIES.map(([label, Icon], i) => (
            <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="h-9 w-9 rounded-lg bg-blue-600/10 flex items-center justify-center shrink-0">
                <Icon className="h-4.5 w-4.5 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-slate-700">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Team / personas */}
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">Meet your always-on AI marketing team</h3>
          <p className="mt-3 text-slate-600">Specialists that learn your brand once, then execute forever.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="marketing-team-personas">
          {TEAM.map((m, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-blue-300 transition-colors">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold tracking-wide">
                  {m.initials}
                </div>
                <div>
                  <div className="font-bold text-slate-900 leading-tight">{m.name}</div>
                  <div className="text-xs font-medium text-blue-600 flex items-center gap-1">
                    <m.icon className="h-3.5 w-3.5" /> {m.role}
                  </div>
                </div>
              </div>
              <p className="mt-4 text-sm text-slate-600">{m.blurb}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
